import { Component, OnInit, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs/';

@Component({
    selector: '',
    template: ''
})
export abstract class RxComponent implements OnInit, OnDestroy {

    private lifecycle: BehaviorSubject<ComponentEvent>;

    constructor() {
        this.lifecycle = new BehaviorSubject(ComponentEvent.Create);
    }

    public getLifecycle(): Observable<ComponentEvent> {
        return this.lifecycle.asObservable();
    }

    ngOnInit() {
        this.lifecycle.next(ComponentEvent.Init);
    }

    ngOnDestroy() {
        this.lifecycle.next(ComponentEvent.Destroy);
    }
}

export enum ComponentEvent {
    Create, Init, Destroy
}

declare module 'rxjs/Observable' {

    interface Observable<T> {

        /**
         * By binding to the lifecycle of the component, you guarantee that the observable will
         * properly complete (unsubscribe) after the component's ngOnDestroy() hook is called.
         *
         * **Note**: The component must extend the base RxComponent class. If you implement ngOnDestroy() yourself,
         * don't forget to call super.ngOnDestroy()!
         * @param {RxComponent} component - the component whose lifecycle you want to bind the observable to.
         */
        bindToLifecycle(component: RxComponent): Observable<T>;

        /**
         * Catches if an error is emitted by the source observable and calls onComplete() instead.
         */
        neverError(action?: (err) => void): Observable<T>;

        /**
         * Waits for the given observable to emit an item.
         */
        waitUntil(until: Observable<T>): Observable<T>;

        /**
         * Caches the latest N emissions from the source observable. Default: 1.
         */
        cacheResult(bufferSize?: number): Observable<T>;

        /**
         * Executes the action for every item or error emitted by the source observable.
         */
        doOnEach(action: () => void): Observable<T>;

        /**
         * Maps each value emitted with an index;
         */
        mapWithIndex<T>(): Observable<IndexWrapper<T>>;

        /**
         * Maps each value emitted to a **StreamEvent** object holding 
         * the emitted value and the provided event. 
         */
        mapToEvent<T>(type: string): Observable<StreamEvent<T>>;
    }
}

export class StreamEvent<T> {

    constructor(
        public readonly type: string,
        public readonly event: T,
    ) { }

    static create<T>(type: string, event: T) {
        return new StreamEvent(type, event);
    }
}

export class IndexWrapper<T> {

    private readonly _index: number;
    private readonly _value: T;

    constructor(index: number, value: T) {
        this._index = index;
        this._value = value;
    }

    get index(): number {
        return this._index;
    }

    get value(): T {
        return this._value;
    }
}

Observable.prototype.bindToLifecycle = function (component: RxComponent): Observable<any> {
    let source = this;

    return source.takeUntil(
        component.getLifecycle().filter(e => e === ComponentEvent.Destroy)
    );
};

Observable.prototype.neverError = function (action: (err) => void): Observable<any> {
    let source = this;

    return source.catch(e => {
        if (action) {
            action(e);
        }

        return Observable.empty();
    });
};

Observable.prototype.waitUntil = function(until: Observable<any>): Observable<any> {
    let source = this;

    return until.take(1).flatMap(_ => source);
};

Observable.prototype.doOnEach = function (action: () => void): Observable<any> {
    let source = this;

    return source.do(
        item => action(),
        err => action(),
    );
};

Observable.prototype.cacheResult = function (bufferSize: number = 1): Observable<any> {
    let source = this;

    return source.publishReplay(bufferSize).refCount();
};

Observable.prototype.mapWithIndex = function<T>(): Observable<IndexWrapper<T>> {
    let source = this;

    let index = 0;
    return source.map(x => new IndexWrapper(index++, x));
};

Observable.prototype.mapToEvent = function<T>(event: string): Observable<StreamEvent<T>> {
    let source = this;
    
    return source.do(_ => console.log('mapping to event ' + event)).map((data: T) => new StreamEvent<T>(event, data));
};


// WEBPACK FOOTER //
// ./src/app/shared/components/common/rx.component.ts